import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, Eye, EyeOff } from "lucide-react";
import { trackLogin } from "@/lib/analytics";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("mobile"); // mobile | email
  const [mobile, setMobile] = useState("");  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [role, setRole] = useState("worker");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [workerCount, setWorkerCount] = useState(null);
  React.useEffect(() => { api.get("/stats/worker-count").then((r) => setWorkerCount(r.data)).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!/^\d{6,15}$/.test(mobile)) return toast.error("Enter valid mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { mobile });
      setOtpSent(true);
      if (data?.via === "sms") {
        toast.success("OTP sent to your mobile");
      } else if (data?.fallback_reason === "twilio_daily_limit") {
        toast.error("SMS quota reached for today. Please use Email login or try again after 12 AM.", { duration: 8000 });
      } else if (data?.demo_otp) {
        toast.info(`SMS unavailable — dev OTP: ${data.demo_otp}`, { duration: 15000 });
      } else {
        toast.success("OTP sent");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { mobile, otp, role });
      login(data);
      trackLogin("otp");
      toast.success("Welcome!");
      nav("/dashboard");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const emailLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      trackLogin("email");
      toast.success("Welcome!");
      nav("/dashboard");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-10 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">{t("login")}</h1>
      <p className="mt-2 text-base font-medium text-sarthi-muted">{t("tagline")}</p>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          data-testid="mode-mobile"
          onClick={() => { setMode("mobile"); setOtpSent(false); }}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-4 text-base font-bold ${mode === "mobile" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-text"}`}
        >
          <Phone className="h-5 w-5" /> {t("login_mobile")}
        </button>
        <button
          data-testid="mode-email"
          onClick={() => setMode("email")}
          className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-4 text-base font-bold ${mode === "email" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-text"}`}
        >
          <Mail className="h-5 w-5" /> {t("login_email")}
        </button>
      </div>

      <div className="sarthi-card mt-6 p-6">
        <GoogleSignInButton testid="login-google-btn" />
        <div className="my-4 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-sarthi-muted">
          <div className="h-px flex-1 bg-sarthi-border" /> or <div className="h-px flex-1 bg-sarthi-border" />
        </div>
        {mode === "mobile" ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("mobile_number")}</label>
              <input
                data-testid="input-mobile"
                inputMode="numeric"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                placeholder="9999900001"
                className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-4 text-lg font-semibold focus:border-sarthi-primary focus:outline-none"
                disabled={otpSent}
              />
            </div>
            {!otpSent && (
              <div>
                <label className="mb-2 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("select_role")}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button data-testid="role-worker" onClick={() => setRole("worker")} className={`rounded-xl border-2 py-3 font-bold ${role === "worker" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}>{t("worker")}</button>
                  <button data-testid="role-contractor" onClick={() => setRole("contractor")} className={`rounded-xl border-2 py-3 font-bold ${role === "contractor" ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}>{t("contractor")}</button>
                </div>
              </div>
            )}
            {otpSent && (
              <div>
                <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("enter_otp")}</label>
                <input
                  data-testid="input-otp"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="______"
                  className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] focus:border-sarthi-primary focus:outline-none"
                  maxLength={6}
                />
                <div className="mt-2 flex items-center justify-between text-xs font-semibold">
                  <span className="text-sarthi-muted">Didn't receive?</span>
                  <button
                    type="button"
                    data-testid="resend-otp-btn"
                    onClick={sendOtp}
                    disabled={busy}
                    className="font-bold text-sarthi-primary hover:underline disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            )}
            {!otpSent ? (
              <button data-testid="send-otp-btn" onClick={sendOtp} disabled={busy} className="sarthi-btn-primary flex w-full items-center justify-center gap-2 py-4 text-lg text-white">
                {t("send_otp")} <ArrowRight className="h-5 w-5" />
              </button>
            ) : (
              <button data-testid="verify-otp-btn" onClick={verifyOtp} disabled={busy || otp.length !== 6} className="sarthi-btn-primary flex w-full items-center justify-center gap-2 py-4 text-lg text-white disabled:opacity-50">
                {t("verify")} <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("email")}</label>
              <input data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-4 text-lg font-semibold focus:border-sarthi-primary focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("password")}</label>
              <div className="relative">
                <input
                  data-testid="input-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-4 pr-12 text-lg font-semibold focus:border-sarthi-primary focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-sarthi-muted hover:text-sarthi-primary"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={async () => {
                    if (!email) return toast.error("Enter your email first");
                    try {
                      await api.post("/auth/forgot-password", { email });
                      toast.success("If an account exists for that email, a reset link is on its way.");
                    } catch (e) { toast.error(formatError(e)); }
                  }}
                  className="text-xs font-bold text-sarthi-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>
            <button data-testid="email-login-btn" onClick={emailLogin} disabled={busy || !email || !password} className="sarthi-btn-primary flex w-full items-center justify-center gap-2 py-4 text-lg text-white disabled:opacity-50">
              {t("login")} <ArrowRight className="h-5 w-5" />
            </button>
            {workerCount?.workers > 0 && (
              <div data-testid="worker-count-social-proof" className="mt-4 rounded-xl border-2 border-sarthi-border bg-sarthi-accent/40 p-3 text-center text-sm font-bold text-sarthi-secondary">
                Join <span className="text-sarthi-primary">{workerCount.workers.toLocaleString("en-IN")}</span> workers already on Sarthi Associates
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 text-center text-base font-semibold text-sarthi-muted">
        {t("new_here")} <Link to="/register" data-testid="go-register" className="font-bold text-sarthi-primary underline">{t("create_account")}</Link>
      </div>
    </div>
  );
}